import React, { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
  useNavigationType,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { getLoginRedirectPath } from './lib/browserE2eAuth';
import { queryClient } from './lib/queryClient';
import { TelegramProvider } from './contexts/TelegramContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { useAuthStore } from './stores/authStore';
import { LoginPage } from './pages/LoginPage';
import { HomeRedirect } from './pages/HomeRedirect';
import {
  LazyWeeksPage,
  LazyProfilePage,
  LazyAboutPage,
  LazyNotificationSettingsPage,
  LazyCreateWeekPage,
  LazyWeekDetailPage,
  LazyWeekSetupWizard,
  LazySkillsSelectionPage,
  LazySelfEnquiryPage,
  LazySelfEnquiryHistoryPage,
  LazySelfEnquiryJournalDetailPage,
  LazySocialSignalsTrackingPage,
  LazyTrackerPage,
  LazyPrivateBehaviorsTrackingPage,
  LazyRiskFactorsTrackingPage,
  LazyRadicallyOpenSkillsTrackingPage,
  LazySelfEnquiryTrackingPage,
  LazyArchivePage,
  LazyArchiveWeekReviewPage,
  LazyJournalPage,
} from './routes/lazyPages';

/** Сброс прокрутки при PUSH/REPLACE; при POP позицию не трогаем (возврат «Назад»). */
const ScrollToTop: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();
  useEffect(() => {
    if (navigationType === 'POP') return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, search, hash, navigationType]);
  return null;
};

const RequireAuth: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to={getLoginRedirectPath(location.search)} replace />;
  }
  return <Outlet />;
};

function AppRoutes() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireAuth />}>
          <Route element={<Layout />}>
            <Route index element={<HomeRedirect />} />
            <Route path="profile" element={<LazyProfilePage />} />
            <Route path="about" element={<LazyAboutPage />} />
            <Route path="settings/notifications" element={<LazyNotificationSettingsPage />} />
            <Route path="weeks/create" element={<LazyCreateWeekPage />} />
            <Route path="weeks/:id/setup" element={<LazyWeekSetupWizard />} />
            <Route path="weeks/:id/social-signals-tracking" element={<LazySocialSignalsTrackingPage />} />
            <Route path="weeks/:id/private-behaviors-tracking" element={<LazyPrivateBehaviorsTrackingPage />} />
            <Route path="weeks/:id/risk-factors-tracking" element={<LazyRiskFactorsTrackingPage />} />
            <Route path="weeks/:id/radically-open-skills-tracking" element={<LazyRadicallyOpenSkillsTrackingPage />} />
            <Route path="weeks/:id/self-enquiry-tracking" element={<LazySelfEnquiryTrackingPage />} />
            <Route path="weeks/:weekId/skills" element={<LazySkillsSelectionPage />} />
            <Route path="weeks/:id" element={<LazyWeekDetailPage />} />
            <Route path="weeks" element={<LazyWeeksPage />} />
            <Route path="tracker" element={<LazyTrackerPage />} />
            <Route path="journal" element={<LazyJournalPage />} />
            <Route path="self-enquiry/history" element={<LazySelfEnquiryHistoryPage />} />
            <Route path="self-enquiry/journal/:entryId" element={<LazySelfEnquiryJournalDetailPage />} />
            <Route path="self-enquiry" element={<LazySelfEnquiryPage />} />
            <Route path="archive/weeks/:weekId" element={<LazyArchiveWeekReviewPage />} />
            <Route path="archive" element={<LazyArchivePage />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TelegramProvider>
          <AppRoutes />
        </TelegramProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
